import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Grid, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouteLink} from 'react-router-dom'

// Utils
import {categoryUrlBuilder} from '../../../utils/url'

// Custom Hooks
import useMultiSite from '../../../hooks/use-multi-site'

/**
 * A React functional component that renders a list of additional links.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Array} props.links - An array of link objects to be rendered.
 * @param {string} props.links[].id - The unique identifier for each link.
 * @param {string} props.links[].url - The URL that the link points to.
 * @param {string} props.links[].name - The display name of the link.
 *
 * @returns {JSX.Element} A JSX element containing a list of headings, each with a link.
 *
 * @example
 * const links = [
 *   { id: '1', url: '/home', name: 'Home' },
 *   { id: '2', url: '/about', name: 'About' }
 * ];
 * <AdditionalLinks links={links} />
 */
const AdditionalLinks = ({links}) => {
    const {buildUrl} = useMultiSite()

    /**
     * Memoizes a list of links with updated URLs.
     *
     * This function utilizes the `useMemo` hook to optimize performance by memoizing
     * the transformation of a list of link objects. Each link object in the `links` array
     * is mapped to a new object that includes all original properties of the link, but with
     * the `url` property transformed using the `buildUrl` function. The memoization ensures
     * that the transformation is only recalculated when the `links` array or the `buildUrl`
     * function changes.
     *
     * @returns {Array} An array of link objects with updated URLs.
     */
    const memoizedLinks = useMemo(() => {
        return links.map((link) => ({
            ...link,
            url: link.url
        }))
    }, [links, buildUrl])

    return (
        <Box as="li" mb={10} paddingLeft={20} listStyleType="none">
            {memoizedLinks.map((link) => (
                <Heading
                    as="div"
                    size="2xl"
                    textTransform="uppercase"
                    key={link.id}
                    style={{textDecoration: 'underline'}}
                    mb={10}
                >
                    <Link as={RouteLink} to={link.url} size={'2xl'} padding="0">
                        {link.name}
                    </Link>
                </Heading>
            ))}
        </Box>
    )
}

AdditionalLinks.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    ).isRequired
}

/**
 * A React component that renders a category and its subcategories in a hierarchical structure.
 *
 * This component displays a category name as a heading, with the size of the heading determined
 * by the category's level. Subcategories, if present, are rendered recursively as nested Category components.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.category - The category object to be rendered.
 * @param {string} props.category.name - The name of the category.
 * @param {number} props.category.level - The hierarchical level of the category, which determines the heading size.
 * @param {Array} props.category.subcategories - An array of subcategory objects, each of which will be rendered as a nested Category component.
 *
 *
 * @returns {JSX.Element} The rendered category component.
 */
const Category = ({category}) => {
    // eslint-disable-next-line react/prop-types
    const {name, level, subcategories, id, categoryType} = category

    const headingSize = {
        1: '2xl',
        2: 'xl',
        3: 'md',
        4: 'md'
    }[level]

    return (
        <Box as="li" mb={2} paddingLeft={20} listStyleType="none">
            <Heading
                as={`h${level + 1}`}
                size={headingSize}
                textTransform="uppercase"
                mb={level == 1 ? 10 : 4}
            >
                <Link
                    as={RouteLink}
                    to={categoryUrlBuilder({id: id}, categoryType)}
                    style={{textDecoration: 'underline'}}
                    size={headingSize}
                    padding="0"
                >
                    {name}
                </Link>
            </Heading>
            {subcategories?.length > 0 && (
                <Box as="ul" mb={10} listStyleType="none">
                    {subcategories?.map((subcat) => (
                        <Box as="li" key={subcat.id}>
                            <Category category={subcat} />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

Category.propTypes = {
    category: PropTypes.shape({
        name: PropTypes.string.isRequired,
        level: PropTypes.number.isRequired,
        subcategories: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
                level: PropTypes.number.isRequired,
                subcategories: PropTypes.array
            })
        ).isRequired
    }).isRequired
}

/**
 * SitemapBuilder is a functional React component that renders a grid layout
 * for displaying a list of categories. It utilizes the Chakra UI Grid component
 * to create a responsive layout that adjusts based on the screen size.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.data - An array of category objects to be displayed in the sitemap.
 * Each category object should have a unique 'id' property.
 *
 * @returns {JSX.Element} A JSX element representing the grid layout of categories.
 *
 * @example
 * const categories = [
 *   { id: 1, name: 'Category 1' },
 *   { id: 2, name: 'Category 2' },
 *   { id: 3, name: 'Category 3' }
 * ];
 *
 * <SitemapBuilder data={categories} />
 *
 * The above example will render a grid with three categories, each represented
 * by a Category component.
 */
const SitemapBuilder = ({data, additionalLinks}) => {
    /**
     * Memoizes the list of category components to optimize rendering performance.
     *
     * This hook utilizes the `useMemo` React hook to memoize the result of mapping
     * over the `data` array, which contains category objects. Each category object
     * is transformed into a `Category` component, with the `key` prop set to the
     * category's unique `id` and the `category` prop set to the category object itself.
     *
     * By providing `data` as a dependency array, the memoized value will only be
     * recalculated when the `data` array changes, thus preventing unnecessary re-renders
     * of the category components when the `data` array remains unchanged.
     *
     * @returns {Array} An array of `Category` components, each representing a category
     *                  from the `data` array.
     */
    const memoizedCategories = useMemo(() => {
        return data.map((category) => <Category key={category.id} category={category} />)
    }, [data])
    return (
        <>
            <Box
                as="ul"
                sx={{
                    columnWidth: 'auto',
                    columnCount: {base: 1, md: 3, lg: 4}
                }}
                gap={20}
                marginRight={20}
                marginBottom={40}
                textAlign="left"
            >
                {memoizedCategories}
                <AdditionalLinks links={additionalLinks} />
            </Box>
        </>
    )
}

SitemapBuilder.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            level: PropTypes.number.isRequired,
            subcategories: PropTypes.array
        })
    ).isRequired,
    additionalLinks: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    ).isRequired
}

export default SitemapBuilder
